import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { UserModel } from '@common/models/User.model';
import { AuthenticationService } from '@common/services/authentication.service';
import { CustomIconService } from '@common/services/custom-icon.service';
import { InstanceTrackerService } from '@common/services/instance-tracker.service';
import { KendoMessageService } from '@common/services/kendoMessage.service';
import { StartupService } from '@common/services/startup.service';
import { currentUserSubject, UserService } from '@common/services/user.service';
import { marker } from '@common/translate-marker';
import { isMobile } from '@common/utils/isMobile';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from '@progress/kendo-angular-l10n';
import { ToastService } from '@services/toast.service';
import { TitleService } from './common/services/title.service';
import { ChatCounterService } from './singleton-services/chat-counter.service';
import { StateService } from './singleton-services/state.service';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
    private static readonly publicRoutes = ['/forgotPassword', '/resetPassword', '/login', '/logout'];

    // routes that make no sense as a post-login landing page
    private static readonly noReturnUrlRoutes = ['/error', '/certError'];

    currentUser: UserModel;
    isSidebarCollapsed = false;

    get isAuthenticated() {
        return !!this.currentUser;
    }

    get isImpersonating() {
        return (
            this.currentUser &&
            this.currentUser.signInType === 'IMPERSONATION' &&
            sessionStorage.getItem('impersonationOriginalToken') !== null
        );
    }

    get isBusy() {
        return this.startupService.isBusy;
    }

    get setupStatus() {
        return this.startupService.status;
    }

    constructor(
        private router: Router,
        private translateService: TranslateService,
        private startupService: StartupService,
        private messageService: MessageService,
        private pingService: InstanceTrackerService,
        private authenticationService: AuthenticationService,
        private chatCounterService: ChatCounterService,
        private customIconService: CustomIconService,
        private userService: UserService,
        private toastyService: ToastService,
        private stateService: StateService,
        titleService: TitleService
    ) {
        this.customIconService.registerCustomIcons();

        // title update subscribe
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const title = this.getDeepestTitle(this.router.routerState.snapshot.root);
                titleService.setTitle(title);
            }
        });

        // subscribe to current user updates
        currentUserSubject.subscribe((value) => {
            // the emitted user is always mirrored, including the null pushed on logout. Every session
            // timeout path sets 'loggedout' *before* emitting that null, so gating this assignment on
            // the flag kept a stale user on the component - which is what left the impersonation
            // banner ("You are currently logged in as: ...") visible on the login page after a timeout.
            this.currentUser = value;

            // Start chat counter polling when user is authenticated
            if (value) {
                this.chatCounterService.startPolling();
            } else {
                this.chatCounterService.stopPolling();
            }

            const loggedout = localStorage.getItem('loggedout');
            if (loggedout == 'true') {
                // if user is not logged in and is viewing page that's not public, redirect to login page.
                // '/logout' is excluded on purpose: LogoutComponent already owns the /logout -> /login
                // redirect, so navigating here as well produces a second, racing navigation - the source
                // of the NG04008 router error seen on session expiry.
                const path = window.location.pathname;

                const isPublicRoute = AppComponent.publicRoutes.some((route) => path.startsWith(route));

                if (!isPublicRoute) {
                    const returnUrl = this.getLoginReturnUrl();
                    if (returnUrl) {
                        this.router.navigate(['/login'], { queryParams: { returnUrl } });
                    } else {
                        this.router.navigate(['/login']);
                    }
                }
            }
        });
    }

    ngOnInit() {
        this.checkLanguage();
        this.updateKendoMessageTranslations();
        this.checkPingStatus();
        this.pingService.startPing();
    }

    exitImpersonation() {
        this.userService.exitImpersonation().subscribe({
            next: (result: { originalSignInType: string; originalLanguage: string }) => {
                this.stateService.setSignInType(result.originalSignInType);

                // Restore original tokens
                const originalAccessToken = sessionStorage.getItem('impersonationOriginalToken');
                const originalRefreshToken = sessionStorage.getItem('impersonationOriginalRefreshToken');

                if (originalAccessToken && originalRefreshToken) {
                    this.userService.setAuthenticationTokens(originalAccessToken, originalRefreshToken);
                }

                // Restore original language if provided by server
                if (result.originalLanguage) {
                    localStorage.setItem('language', result.originalLanguage);
                    this.translateService.use(result.originalLanguage.toLowerCase());

                    const msgs = <KendoMessageService>this.messageService;
                    msgs.language = result.originalLanguage.toLowerCase();
                }

                // Clear impersonation session storage
                this.authenticationService.clearImpersonationState();

                // Reload current user
                this.authenticationService.loadCurrentUser().then(() => {
                    this.router.navigate(['/administration/user/list']);
                    this.toastyService.success(
                        this.translateService.instant(marker('Successfully returned to administrator account'))
                    );
                });
            },
            error: (error) => {
                this.toastyService.error(
                    this.translateService.instant(marker('Error returning to administrator account'))
                );
                console.error('Returning to administrator account error:', error);
            }
        });
    }

    /** Current browser url to return to after login, or null when the page is not a
     *  meaningful post-login target (public/error pages, root). */
    private getLoginReturnUrl(): string {
        const path = window.location.pathname;
        const excluded = [...AppComponent.publicRoutes, ...AppComponent.noReturnUrlRoutes];
        if (path === '/' || excluded.some((route) => path.startsWith(route))) {
            return null;
        }
        return path + window.location.search;
    }

    private getDeepestTitle(routeSnapshot: ActivatedRouteSnapshot) {
        let title = routeSnapshot.data ? routeSnapshot.data['title'] : '';

        if (routeSnapshot.firstChild) {
            title = this.getDeepestTitle(routeSnapshot.firstChild) || title;
        }

        return title;
    }

    getMainContentMargin(isCollapsed) {
        if (this.isAuthenticated) {
            if (isMobile()) {
                return '0px';
            }

            if (isCollapsed) {
                return '70px';
            } else {
                return '260px';
            }
        } else {
            return '0px';
        }
    }

    private checkPingStatus() {
        const ping = localStorage.getItem('ping');

        if (ping) {
            const lastPingTime = new Date(ping);
            const currentTime = new Date();
            const timeDifference = (currentTime.getTime() - lastPingTime.getTime()) / 1000; // in seconds

            if (timeDifference > 120) {
                localStorage.setItem('loggedout', 'true');
                const returnUrl = this.getLoginReturnUrl();
                if (returnUrl) {
                    // LogoutComponent forwards returnUrl to the login page
                    this.router.navigate(['/logout'], { queryParams: { returnUrl } });
                } else {
                    this.router.navigate(['/logout']);
                }
            }
        }
    }

    private checkLanguage() {
        localStorage.setItem(
            'language',
            (localStorage.getItem('language') || this.currentUser?.language.id || 'BG').toUpperCase()
        );
    }

    private updateKendoMessageTranslations() {
        const lang = localStorage.getItem('language') || 'EN';
        this.translateService.use(lang.toLowerCase());
        const msgs = <KendoMessageService>this.messageService;
        msgs.language = lang.toLowerCase();
    }
}
